// The motion layer (Definitive Redesign, Part 2). Runs last in index.html,
// after homepage.js/reveal.js/before-after.js have built and wired the DOM.
// Everything here is additive on top of that — nothing here is required
// for the page to be correct and readable; prefers-reduced-motion turns
// almost all of it off and the page must still be fully beautiful static.
//
// Split into small init*() functions, each independently guarded, so a
// missing library or an unsupported browser degrades one effect instead
// of throwing and killing the rest.

(function () {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  const isDesktop = window.matchMedia("(min-width: 821px)").matches;

  // ---- Lenis smooth scroll (§2.1) — the foundation everything else rides.
  // Disabled outright under reduced-motion: native scroll is the correct
  // "static" fallback, not a slowed-down version of the smoothing.
  let lenis = null;
  function initLenis() {
    if (reducedMotion || typeof Lenis === "undefined") return null;
    lenis = new Lenis({
      duration: 1.1,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
    });
    // Drive Lenis off EITHER its own rAF loop OR GSAP's ticker, never
    // both — this used to call lenis.raf() from both a dedicated
    // requestAnimationFrame loop AND gsap.ticker.add() simultaneously,
    // updating its internal position/velocity twice per real frame with
    // two different timestamp sources. That's what caused scroll-driven
    // effects (most visible on the gallery's continuous drift) to lag
    // behind the actual scroll gesture and only "settle" once scrolling
    // stopped — a desync between two competing clocks, not a smoothing
    // duration issue. GSAP's ticker is itself one rAF loop already
    // shared by every other animation on the page, so when it's
    // available that's the single clock everything (including Lenis)
    // rides; the standalone loop is now only a fallback for the
    // no-GSAP case.
    if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
    }
    return lenis;
  }

  // ---- Custom cursor (§2.3) — mix-blend-mode:difference dot that follows
  // the pointer. Desktop + fine-pointer only; a synthetic cursor on touch
  // is just visual noise no one asked for.
  function initCursor() {
    const dot = document.getElementById("cursor-dot");
    if (!dot || reducedMotion || isTouch || !isDesktop) return;
    document.documentElement.classList.add("has-custom-cursor");
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let cx = x;
    let cy = y;
    window.addEventListener("mousemove", (e) => {
      x = e.clientX;
      y = e.clientY;
      dot.style.opacity = "1";
    }, { passive: true });
    document.addEventListener("mouseleave", () => { dot.style.opacity = "0"; });
    function follow() {
      cx += (x - cx) * 0.18;
      cy += (y - cy) * 0.18;
      dot.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
      requestAnimationFrame(follow);
    }
    requestAnimationFrame(follow);

    const grow = "a, button, .cursor-grow";
    document.addEventListener("mouseover", (e) => {
      if (e.target.closest(grow)) dot.classList.add("is-grown");
    });
    document.addEventListener("mouseout", (e) => {
      if (e.target.closest(grow)) dot.classList.remove("is-grown");
    });
  }

  const hasGsapScroll = typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  if (hasGsapScroll) gsap.registerPlugin(ScrollTrigger);
  // Pure performance tuning, zero visual change — both are GSAP's own
  // documented recommendations for a page with many ScrollTrigger
  // instances (18 on this homepage). `limitCallbacks` stops onEnter/
  // onLeave/onUpdate-style callbacks from re-firing redundantly on every
  // tick when a trigger's state hasn't actually changed (doesn't affect
  // scrub progress itself, which is unconditional regardless of this
  // flag). `ignoreMobileResize` stops the whole page's ScrollTriggers
  // from recalculating (`refresh()`) every time a mobile browser's
  // address bar shows/hides mid-scroll, which otherwise looks like a
  // resize event but isn't one worth reacting to.
  if (hasGsapScroll) ScrollTrigger.config({ limitCallbacks: true, ignoreMobileResize: true });

  // ---- The Philosophy section's pinned, scroll-scrubbed two-panel
  // sequence (Revision Pass 6). Pins the moment its top hits the viewport
  // top, for ~200vh, and maps the whole scrubbed timeline 1:1 to the
  // brief's own 0–1 progress map:
  //   0.00–0.12  light eyebrow + tick + "01" rise in
  //   0.12–0.35  light display line mask-reveals (transform only)
  //   0.35–0.52  light italic line mask-reveals
  //   0.52–0.66  hold
  //   0.66–1.00  aperture wipe (a circular clip-path closing on the eye
  //              motif's own measured position) while the dark panel's
  //              eyebrow/numeral/lines mask-reveal in the same stagger,
  //              proportionally compressed into that window
  // GSAP's free core has no CustomEase (that's a paid Club GreenSock
  // plugin, not loaded here), so arbitrary cubic-bezier curves aren't
  // available as tween eases — "power4.out" is the closest built-in match
  // to the brief's cubic-bezier(0.22, 1, 0.36, 1) (the same curve already
  // aliased sitewide as --ease for CSS transitions): a hard, pure
  // deceleration with no bounce or elastic, just like the brief asks.
  // Desktop only (Part 7: mobile drops pin/parallax-class effects) —
  // reduced-motion gets its own explicit branch below (a plain opacity
  // fade between static, normally-flowed panels, no pin/scrub/wipe) per
  // the brief, rather than the sitewide convention of just bailing
  // entirely under reduced motion.
  const PHIL_EASE = "power4.out";
  function initPhilosophySequence() {
    if (!hasGsapScroll) return;
    const pin = document.getElementById("phil-pin");
    const light = document.getElementById("phil-light");
    const dark = document.getElementById("phil-dark");
    if (!pin || !light || !dark) return;

    if (reducedMotion) {
      gsap.set([light, dark], { opacity: 0 });
      [light, dark].forEach((panel) => {
        gsap.to(panel, {
          opacity: 1,
          duration: 1,
          ease: "none",
          scrollTrigger: { trigger: panel, start: "top 85%", toggleActions: "play none none none" },
        });
      });
      return;
    }
    if (!isDesktop) return;

    const eyebrowA = document.getElementById("phil-eyebrow-a");
    const eyebrowB = document.getElementById("phil-eyebrow-b");
    const numeralA = light.querySelector(".phil-numeral");
    const numeralB = dark.querySelector(".phil-numeral");
    const tickA = light.querySelector(".phil-tick");
    const tickB = dark.querySelector(".phil-tick");
    const eye = document.getElementById("phil-eye");
    const a1 = light.querySelector(".phil-display");
    const a2 = light.querySelector(".phil-italic");
    const b1 = dark.querySelector(".phil-display");
    const b2 = dark.querySelector(".phil-italic");
    if (!a1 || !a2 || !b1 || !b2) return;

    pin.classList.add("js-sequenced");

    // The wipe closes on the eye motif's own on-screen center — measured
    // once, as a percentage of the light panel's own box (not raw px),
    // so it holds up at any viewport size. Safe to measure now: layout is
    // stable right up until the pin engages.
    let originX = 50;
    let originY = 50;
    if (eye) {
      const panelRect = light.getBoundingClientRect();
      const eyeRect = eye.getBoundingClientRect();
      originX = ((eyeRect.left + eyeRect.width / 2 - panelRect.left) / panelRect.width) * 100;
      originY = ((eyeRect.top + eyeRect.height / 2 - panelRect.top) / panelRect.height) * 100;
    }
    const clipOpen = `circle(150% at ${originX.toFixed(1)}% ${originY.toFixed(1)}%)`;
    const clipShut = `circle(0% at ${originX.toFixed(1)}% ${originY.toFixed(1)}%)`;

    gsap.set(light, { clipPath: clipOpen });
    gsap.set([a1, a2, b1, b2], { yPercent: 105 });
    // The light panel's own eyebrow/tick/"01" are NOT part of the hidden
    // set — they're visible the moment the section scrolls into view
    // (their CSS resting opacity: .55, full, .07), a quiet call to keep
    // scrolling rather than a blank panel that only starts showing
    // anything once the reader is already deep into the pinned range.
    // Only the dark panel's matching trio (revealed later, at the wipe)
    // and the eye motif start hidden.
    gsap.set([eyebrowB, numeralB, tickB], { opacity: 0 });
    if (eye) gsap.set(eye, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: pin,
        start: "top top",
        end: "+=200%",
        scrub: 0.5,
        pin: true,
        // Pure perf tuning, zero visual change — GSAP's own documented
        // fix for pinned triggers: without it, a fast scroll can land
        // past the pin's start point before ScrollTrigger has pinned it,
        // causing one extra forced reflow to correct course. Anticipating
        // by 1 (a fixed velocity-based lookahead, not a duration) engages
        // the pin slightly early on fast scrolls so that correction never
        // has to happen. This is the single most expensive ScrollTrigger
        // on the page (the only real pin:true on the homepage), so it's
        // the one most worth this.
        anticipatePin: 1,
      },
    });

    // 0.00–0.12 — the eye motif fades in alongside the line reveals below
    // (the eyebrow/tick/"01" are already visible, see above).
    if (eye) tl.to(eye, { opacity: 0.14, ease: PHIL_EASE, duration: 0.12 }, 0);
    // Parallax: the ghost numeral drifts continuously across the whole
    // pin — a translateY tween, independent of its (already-resting)
    // opacity — at a visibly slower rate than the lines' own (much
    // larger, much quicker) mask-reveal motion below.
    tl.to(numeralA, { y: -40, ease: "none", duration: 1 }, 0);

    // 0.12–0.35 / 0.35–0.52 — the two lines mask-reveal in turn. Pure
    // transform, no opacity — a reveal, not a fade.
    tl.to(a1, { yPercent: 0, ease: PHIL_EASE, duration: 0.23 }, 0.12);
    tl.to(a2, { yPercent: 0, ease: PHIL_EASE, duration: 0.17 }, 0.35);

    // 0.52–0.66 — nothing scheduled: the hold. Whatever's already
    // revealed just sits, read.

    // 0.66–1.00 — the aperture wipe: the light panel's circular clip
    // closes over the whole window while the dark panel's own eyebrow,
    // "02", and two lines mask-reveal in the same relative stagger,
    // compressed to fit.
    tl.to(light, { clipPath: clipShut, ease: "none", duration: 0.34 }, 0.66);
    tl.to(tickB, { opacity: 1, ease: PHIL_EASE, duration: 0.09 }, 0.66);
    tl.to(eyebrowB, { opacity: 0.55, ease: PHIL_EASE, duration: 0.09 }, 0.66);
    tl.to(numeralB, { opacity: 0.07, ease: PHIL_EASE, duration: 0.09 }, 0.66);
    tl.to(numeralB, { y: -32, ease: "none", duration: 0.34 }, 0.66);
    tl.to(b1, { yPercent: 0, ease: PHIL_EASE, duration: 0.15 }, 0.74);
    tl.to(b2, { yPercent: 0, ease: PHIL_EASE, duration: 0.12 }, 0.85);

    // Exit: "long after you've forgotten why it's there." dissolves away
    // as the panel scrolls off after the pin releases, rather than just
    // flatly scrolling off with the rest of the page — per explicit
    // request. Can't just extend the scrub timeline above — it's already
    // packed right up to progress 1.0 (b2's own reveal finishes at 0.97,
    // right as the pin releases) — so this is a second, independent
    // ScrollTrigger instead.
    //
    // First attempt used trigger:dark with start:"top top"/end:"bottom
    // top", reasoning that "top top" would stay continuously true for the
    // whole pinned duration and only start advancing once real scrolling
    // resumed. Confirmed WRONG by direct inspection (ScrollTrigger.getAll()
    // in the browser): GSAP computes a plain (non-pinned) trigger's start/
    // end from its bounding box as if it moves 1:1 with scroll the entire
    // time — it has no notion that `dark` sits frozen in place for the
    // whole time its ancestor `pin` is separately pinned. The computed
    // end landed INSIDE the still-pinned range (1056px, well before the
    // pin's own real end at 1584px), so the fade would have finished
    // playing out while the text was still visually frozen mid-pin,
    // finishing before the real scroll-away motion even began.
    //
    // Fixed by anchoring directly to the pin's own resolved end position
    // (`tl.scrollTrigger.end`, read after that ScrollTrigger has already
    // been created above) rather than asking GSAP to infer it from
    // `dark`'s own layout. Function-based start/end (not raw numbers) so
    // this stays correct across ScrollTrigger.refresh() (e.g. a resize).
    // Blur+opacity — the site's own established "grain" dissolve
    // (.reveal-el, same 8px blur value, see CLAUDE.md's motion
    // vocabulary) run in reverse — rather than a translateY move: b2 sits
    // inside a tight per-line overflow:hidden mask (.mask-line-clip,
    // sized to exactly one line of text), so any real vertical travel
    // would just get clipped off almost immediately; a dissolve reads
    // cleanly regardless of that mask.
    // b1 ("We design for the feeling that stays") gets the same dissolve
    // as b2, on the identical trigger range — per explicit follow-up
    // request reverting the earlier stagger — so the two lines dissolve
    // together as one unit rather than one leading the other.
    const pinTrigger = tl.scrollTrigger;
    const EXIT_FADE_PX = window.innerHeight * 0.6; // scroll distance for a full dissolve
    function exitDissolve(el, delayPx) {
      gsap.set(el, { filter: "blur(0px)" });
      gsap.to(el, {
        opacity: 0,
        filter: "blur(8px)",
        ease: "power1.in",
        scrollTrigger: {
          trigger: dark,
          start: () => pinTrigger.end + delayPx,
          end: () => pinTrigger.end + delayPx + EXIT_FADE_PX,
          scrub: true,
        },
      });
    }
    exitDissolve(b2, 0);
    exitDissolve(b1, 0);
  }

  // ---- Masked line reveals (§2.2) — big section statements rise up from
  // behind an overflow:hidden clip instead of just fading in place.
  // SplitType's lineClass gives each line a wrapper we can measure and
  // clip; the clip div is inserted around it (not by SplitType itself,
  // which only splits text, not layout wrappers).
  function initMaskedLines() {
    if (reducedMotion || !hasGsapScroll || typeof SplitType === "undefined") return;
    document.querySelectorAll(".mask-lines").forEach((el) => {
      const split = new SplitType(el, { types: "lines", lineClass: "mask-line" });
      split.lines.forEach((line) => {
        const clip = document.createElement("span");
        clip.className = "mask-line-clip";
        line.parentNode.insertBefore(clip, line);
        clip.appendChild(line);
      });
      gsap.from(split.lines, {
        yPercent: 110,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 85%" },
      });
    });
  }

  // ---- Line-by-line stagger (Process Beat 1's body copy) — a calmer
  // sibling to the masked-line headline reveal above: same SplitType
  // line-splitting, but no clip-mask wrapper (that clipped rise reads as
  // a headline-scale statement; body copy just wants a gentle staggered
  // fade-up per line, not per-word — per-word stagger is reserved for
  // the manifesto).
  // SplitType's line divs are frozen at whatever wrapped at split time —
  // they don't reflow if the column width or font-size changes after
  // (confirmed live: bumping the font-size post-load left the old,
  // now-wrong line breaks sitting there as if they were hard breaks,
  // since each "line" is its own display:block chunk of fixed content).
  // The reveal only needs those wrappers for its own one-time animation,
  // so onComplete reverts SplitType's split, handing the DOM back a
  // single plain text node that reflows normally for anything after.
  function initLineStagger() {
    if (reducedMotion || !hasGsapScroll || typeof SplitType === "undefined") return;
    document.querySelectorAll(".stagger-lines").forEach((el) => {
      const split = new SplitType(el, { types: "lines", lineClass: "stagger-line" });
      gsap.from(split.lines, {
        opacity: 0,
        y: 16,
        duration: 0.7,
        stagger: 0.09,
        ease: "power2.out",
        scrollTrigger: { trigger: el, start: "top 85%" },
        // Real bug (2026-08-21, user report: the reveal "clicks to a
        // final formatting" right after it finishes, "looks like a
        // glitch"). Root-caused live, not guessed: SplitType's own line-
        // detection doesn't always agree with the browser's native
        // wrapping of the same, unsplit text at the same width — verified
        // directly on Process Beat 1's body copy, where SplitType split
        // it into 6 lines but the natural (reverted) paragraph measures
        // out to exactly 5 line-heights (165px / 33.04px-per-line). That
        // ~33px gap is what was popping the instant revert() swapped the
        // DOM from the split's 6-line arrangement back to the true
        // 5-line one — plain split.revert() is a discrete DOM swap, which
        // can never be smoothed by a CSS transition on its own.
        //
        // Fixed defensively rather than by chasing SplitType's own
        // measurement quirk (a third-party library, unlikely to be worth
        // patching, and the exact gap could differ at other viewport
        // widths anyway): measure the height right before and right after
        // revert(), and only when they actually differ, briefly hold the
        // element at the pre-revert height and animate it down/up to the
        // real one instead of letting it snap. Zero effect on the common
        // case where the two heights already agree — this only ever
        // engages exactly when there'd otherwise be a visible pop,
        // regardless of which direction or how large the mismatch is.
        onComplete: () => {
          const beforeHeight = el.getBoundingClientRect().height;
          const prevOverflow = el.style.overflow;
          split.revert();
          const afterHeight = el.getBoundingClientRect().height;
          if (Math.abs(afterHeight - beforeHeight) > 1) {
            el.style.overflow = "hidden";
            gsap.fromTo(el,
              { height: beforeHeight },
              {
                height: afterHeight,
                duration: 0.35,
                ease: "power2.out",
                onComplete: () => { el.style.height = ""; el.style.overflow = prevOverflow; },
              }
            );
          }
        },
      });
    });
  }

  // ---- Ethos line ink-fill (Meliza section, "the ethos moment") — the
  // studio's one-sentence philosophy inks in word-by-word from a dim
  // espresso tone to full cream as the line scrolls through view. Scrub
  // is tied directly to the line's own scroll position (not a fixed
  // duration/ease), so the fill tracks scroll input 1:1 and reads as the
  // line literally resolving into focus — the thematic motion, distinct
  // from the eased one-shot reveals above. gsap.from() only sets the dim
  // starting color when this runs; under reduced motion the block is
  // skipped entirely and the words sit at their plain CSS color
  // (var(--night-ink), full contrast), so there's no flash of dim text.
  function initEthosFill() {
    if (reducedMotion || !hasGsapScroll || typeof SplitType === "undefined") return;
    document.querySelectorAll("[data-ethos-fill]").forEach((el) => {
      const split = new SplitType(el, { types: "words", wordClass: "ethos-word" });
      gsap.from(split.words, {
        color: "oklch(0.42 0.02 62)",
        stagger: 0.06,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          end: "bottom 55%",
          scrub: true,
        },
      });
    });
  }

  // ---- Parallax on large images (§2.2) — .parallax-img wrappers hold an
  // over-scaled (CSS, 1.16x) image; scrubbing its translateY at a slower
  // rate than the page's own scroll is what reads as depth. Desktop only
  // (Part 7: mobile drops parallax), off under reduced-motion.
  function initImageParallax() {
    if (reducedMotion || !isDesktop || !hasGsapScroll) return;
    document.querySelectorAll(".parallax-img").forEach((wrap) => {
      const img = wrap.querySelector("img, video");
      if (!img) return;
      gsap.to(img, {
        yPercent: 8,
        ease: "none",
        scrollTrigger: { trigger: wrap, start: "top bottom", end: "bottom top", scrub: true },
      });
    });
  }

  // ---- Meliza's portrait "float" — the subtle Framer-site effect the
  // user asked for by description: as the page scrolls, the image
  // doesn't track the scroll position 1:1 like the rest of the page —
  // it visibly lags a beat behind, then eases to catch up, reading as
  // weightless/floaty rather than pinned to the document. Technically:
  // parallax with LAG. .parallax-img above (and every other scrubbed
  // effect on this site) uses `scrub:true` — the tween's progress
  // mirrors scroll position exactly, no delay. Passing a NUMBER instead
  // (seconds) tells GSAP to smooth the tween's progress toward the
  // scroll-driven target with that much catch-up easing rather than
  // snapping to it — already used elsewhere on this site for a
  // different reason (initPhilosophySequence's scrub:0.5), but it's the
  // exact same mechanism that produces this effect. Targets the
  // portrait+inset wrapper as one rigid unit, not the image itself —
  // .meliza-cover-img already has its own permanent CSS scale (the
  // zoom-settle reveal) that GSAP doesn't know about; animating a
  // property on an element GSAP doesn't already control avoids any
  // risk of GSAP's own transform write silently resetting that scale.
  // Desktop only, off under reduced motion, matching every other scroll
  // effect on the page.
  function initMelizaPortraitFloat() {
    if (reducedMotion || !isDesktop || !hasGsapScroll) return;
    const wrap = document.querySelector(".meliza-cover-portrait-wrap");
    if (!wrap) return;
    gsap.to(wrap, {
      yPercent: -7,
      ease: "none",
      scrollTrigger: { trigger: wrap, start: "top bottom", end: "bottom top", scrub: 2.2 },
    });
  }

  // ---- Dim-to-bright reveal (§2.2) — a one-time "coming into focus" as a
  // large image enters view, not a continuous scrub. toggleActions plays
  // once and never reverses, so scrolling back up doesn't re-dim it.
  function initDimReveal() {
    if (reducedMotion || !hasGsapScroll) return;
    document.querySelectorAll(".dim-reveal").forEach((img) => {
      gsap.to(img, {
        filter: "brightness(1) saturate(1)",
        duration: 1.3,
        ease: "power2.out",
        scrollTrigger: { trigger: img, start: "top 80%", toggleActions: "play none none none" },
      });
    });
  }

  // ---- Process -> Meliza wash — the handoff into Meliza's section reads
  // as a gradient reveal instead of a hard light->dark cut. Non-pinned
  // scrub-to-scroll-position technique (no pin — the page still scrolls
  // normally). Runs on mobile too,
  // unlike the pinned effects on this page — there's no pin here for
  // mobile to be risky about.
  //
  // Trigger start when `.pb4`'s bottom edge first enters the bottom of the
  // viewport, end when its bottom reaches the viewport top (the exact
  // instant #process's own bottom edge, i.e. the Meliza boundary, is about
  // to enter frame) — a full viewport height of scroll, so the backdrop is
  // already fully dark before the boundary itself ever comes into view,
  // and the wash itself reads as a gradual dissolve instead of a snap.
  // ---- Hero -> Manifesto wash (2026-08-21, per explicit request) — same
  // scroll-scrubbed technique as initProcessToMelizaFade below, but with
  // ease:"power2.in" instead of "none". Hero is exactly 100vh with
  // nothing above it in the document, so unlike every other wash on the
  // page, this trigger's start point is unavoidably scroll position 0 —
  // there's no earlier scroll position to anchor a later start to. A
  // linear scrub from there would visibly tint the hero while the user
  // is still reading the title/sub, which reads as the wash starting too
  // soon rather than as an exit transition. The eased curve keeps opacity
  // negligible through most of the scroll range and only ramps up
  // sharply in the final stretch, right as the user is actually leaving
  // — same trigger geometry as everywhere else, different perceived
  // timing, achieved entirely through easing rather than trigger math.
  function initHeroToManifestoFade() {
    if (reducedMotion || !hasGsapScroll) return;
    const fade = document.getElementById("hero-manifesto-fade");
    const hero = document.querySelector(".hero");
    if (!fade || !hero) return;

    gsap.to(fade, {
      opacity: 1,
      ease: "power2.in",
      scrollTrigger: { trigger: hero, start: "bottom bottom", end: "bottom top", scrub: true },
    });
  }

  function initProcessToMelizaFade() {
    if (reducedMotion || !hasGsapScroll) return;
    const fade = document.getElementById("process-meliza-fade");
    const pb4 = document.querySelector(".pb4");
    if (!fade || !pb4) return;

    gsap.to(fade, {
      opacity: 1,
      ease: "none",
      scrollTrigger: { trigger: pb4, start: "bottom bottom", end: "bottom top", scrub: true },
    });
  }

  // ---- Meliza -> Reflections wash (2026-08-21, per explicit request) —
  // same technique as the Process -> Meliza wash above, color-reversed
  // (see .meliza-reflections-fade, main.css, for the full why). Trigger
  // is #meliza itself, not a sub-element — Meliza doesn't have discrete
  // "beats" the way Process does, same reasoning initReflectionsToCtaFade
  // already uses for Reflections below.
  function initMelizaToReflectionsFade() {
    if (reducedMotion || !hasGsapScroll) return;
    const fade = document.getElementById("meliza-reflections-fade");
    const meliza = document.getElementById("meliza");
    if (!fade || !meliza) return;

    gsap.to(fade, {
      opacity: 1,
      ease: "none",
      // end offset: without it, "bottom top" means the fade only finishes
      // reaching full opacity at the EXACT instant Meliza's real bottom
      // edge (the literal seam with Reflections) reaches the viewport top
      // — zero settle time, so the seam itself is still visible while the
      // wash is mid-transition. Meliza has no natural buffer the way
      // Process does (.pb4's margin-bottom + .process's padding between
      // ITS trigger element and its section's real edge), since the whole
      // section is both the fade's own container and the trigger here, so
      // this reproduces that buffer explicitly via a scroll offset.
      //
      // Follow-up, real bug: a first attempt used a flat 240px offset,
      // verified in this session's own small (~585px-tall) automation
      // window, but the user still saw the break on their real monitor —
      // 240px is only ~22-27% of a real 900-1080px viewport vs. ~41% of
      // this small test window, so the buffer was proportionally much
      // smaller than it looked here. Switched to a percentage of the
      // real viewport height via a function (recalculated on resize,
      // the standard ScrollTrigger pattern for viewport-relative values)
      // — 40% of innerHeight, scaling correctly on any real screen.
      scrollTrigger: {
        trigger: meliza,
        start: "bottom bottom",
        end: () => "bottom top+=" + Math.round(window.innerHeight * 0.4),
        scrub: true,
      },
    });
  }

  // ---- Reflections -> CTA wash — same technique as the Process -> Meliza
  // wash above (replaces the static threshold gradient this used to be),
  // per explicit request to make this handoff a scroll-scrubbed reveal
  // rather than a fixed-looking gradient. Trigger is #reflections itself
  // (not a sub-element — Reflections doesn't have discrete "beats" the way
  // Process does), so the fade spans the section's own full on-screen
  // duration, start to finish, scaling naturally with however tall the
  // card row actually renders.
  function initReflectionsToCtaFade() {
    if (reducedMotion || !hasGsapScroll) return;
    const fade = document.getElementById("reflections-cta-fade");
    const reflections = document.getElementById("reflections");
    if (!fade || !reflections) return;

    gsap.to(fade, {
      opacity: 1,
      ease: "none",
      // end offset: percentage-of-viewport, same fix/reasoning as
      // initMelizaToReflectionsFade above (2026-08-21, superseding an
      // earlier flat-240px attempt that didn't scale to real monitor
      // heights) — this fade had the identical zero-buffer gap between
      // "fully faded" and "seam visible" as that one did.
      scrollTrigger: {
        trigger: reflections,
        start: "bottom bottom",
        end: () => "bottom top+=" + Math.round(window.innerHeight * 0.4),
        scrub: true,
      },
    });
  }

  // ---- Meliza masthead/portrait reveal — previously tied to the wash's
  // own progress (see initProcessToMelizaFade above), which worked when
  // the wash was a short half-viewport scrub finishing right at the
  // boundary. Now that the wash spans a full viewport height, that same
  // 60%-progress mark fires well before either is anywhere near the
  // viewport (the section's own top padding adds further scroll before
  // they're visible) — so their .reveal-el CSS transition (1-1.2s) had
  // long finished by the time they were actually on screen, reading as a
  // flash/pop rather than a visible entrance. Decoupled here into a
  // per-element trigger instead, firing each only once it actually
  // approaches. Portrait deliberately carries plain .reveal-el only (no
  // .dim-reveal, no scale-settle) — just the same opacity+blur fade as
  // the inset photo below it, per explicit request to match that effect.
  function initMelizaCoverReveal() {
    if (reducedMotion || !hasGsapScroll) return;
    document.querySelectorAll(".meliza-cover-timed-reveal").forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        start: "top 85%",
        onEnter: () => el.classList.add("in"),
      });
    });
  }

  // ---- Before/After demo sweep — homepage-only (#ba-home specifically,
  // not the generic .ba component every project page also uses). A brief
  // one-time "peek" once the slider scrolls into view: nudges --pos out
  // to 38% and back to its 16% rest value, demonstrating the drag
  // interaction itself rather than relying on more copy to explain it.
  // Real interaction always wins — cancels itself the instant the user
  // starts dragging, even mid-sweep. Plain object tween (not a DOM
  // property GSAP already owns) since --pos is a custom property that
  // before-after.js itself reads/writes directly via style.setProperty.
  function initBADemoSweep() {
    if (reducedMotion || !hasGsapScroll) return;
    const ba = document.getElementById("ba-home");
    if (!ba) return;
    const state = { pos: 16 };
    let tl;
    const cancel = () => tl && tl.kill();
    ba.addEventListener("mousedown", cancel, { once: true });
    ba.addEventListener("touchstart", cancel, { once: true });
    ScrollTrigger.create({
      trigger: ba,
      start: "top 70%",
      once: true,
      onEnter: () => {
        tl = gsap.timeline({ delay: 0.4 });
        tl.to(state, {
          pos: 38,
          duration: 0.9,
          ease: "power2.inOut",
          onUpdate: () => ba.style.setProperty("--pos", state.pos + "%"),
        }).to(state, {
          pos: 16,
          duration: 0.9,
          ease: "power2.inOut",
          onUpdate: () => ba.style.setProperty("--pos", state.pos + "%"),
        });
      },
    });
  }

  initLenis();
  initCursor();
  initPhilosophySequence();
  // Fraunces/Playfair Display are self-hosted with font-display:swap and no
  // preload hint, so on a cold cache the fallback font is still what's
  // rendering when this script runs. SplitType measures line-wrap positions
  // at split time and freezes them (see initLineStagger's own comment below)
  // — splitting against the fallback's metrics, then having the real font
  // swap in on top of that frozen split, is exactly what read as text (and,
  // via the CSS grid coupling in Process Beat 1/4, the neighboring image
  // column) visibly re-settling into its final position after load. Gated
  // behind document.fonts.ready — same mechanism already used a few lines
  // down for ScrollTrigger's own post-swap refresh — so the split always
  // happens against the fonts that are actually going to render.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      initMaskedLines();
      initLineStagger();
    });
  } else {
    initMaskedLines();
    initLineStagger();
  }
  initEthosFill();
  initImageParallax();
  initMelizaPortraitFloat();
  initDimReveal();
  initHeroToManifestoFade();
  initProcessToMelizaFade();
  initMelizaToReflectionsFade();
  initReflectionsToCtaFade();
  initMelizaCoverReveal();
  initBADemoSweep();

  // Standing ResizeObserver on <body> that re-runs refresh() (debounced)
  // whenever total page height changes for any reason — new pins, lazy
  // images, fonts swapping in — rather than a one-time refresh that's
  // only ever as correct as the DOM was at that exact instant.
  if (hasGsapScroll) {
    ScrollTrigger.refresh();
    // Real bug, root-caused (2026-08-21): Lenis computes its own internal
    // scroll limit once, from whatever the document's height happens to
    // be at that instant, and never re-measures it on its own — it has no
    // ResizeObserver of its own watching body/content height (only a
    // plain `window resize` listener, which a same-size-window content
    // change like a late pin spacer or a font swap never fires). If the
    // real document later grows taller than that stale limit — a
    // ScrollTrigger pin inserting its spacer, a webfont swap reflowing
    // text, any of the reasons this same ResizeObserver already exists —
    // Lenis's own smooth-scroll physically caps out at its old, shorter
    // limit: scrolling just stops short of the new true bottom, even
    // though `ScrollTrigger.refresh()` (which only knows about GSAP's own
    // triggers, not Lenis) has already been called right alongside it.
    // User-reported live: homepage scroll "won't let me scroll all the
    // way down" to the footer. `lenis.resize()` is Lenis's own public API
    // for exactly this — recomputes its internal limit from the current
    // DOM — so it rides along on every refresh this observer already
    // triggers, instead of needing a separate mechanism.
    if (lenis) lenis.resize();
    // Guarded: refresh() recalculates every pin's spacer, and doing
    // that while a pin is actively engaged is a known way to cause a
    // visible jump mid-scroll. Deferring until no pin is active avoids
    // that risk entirely; a trailing resize/font-swap
    // (if the page is still settling) just retries on its own.
    const safeRefresh = () => {
      if (ScrollTrigger.getAll().some((t) => t.isActive)) return;
      ScrollTrigger.refresh();
      if (lenis) lenis.resize();
    };
    let resizeTimer;
    new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(safeRefresh, 200);
    }).observe(document.body);
    // Custom fonts (Fraunces) swapping in from a fallback reflows text
    // line-wrapping sitewide — a precise, guaranteed signal for exactly
    // the kind of one-time height shift that caused the original
    // desync, rather than waiting on the body resize to be noticed.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(safeRefresh);
  }

  // Exposed for the other init*() functions added later in this same file
  // (coverflow, the one gradient fade) so they can all register with the
  // same Lenis-driven ScrollTrigger instance instead of each guessing at
  // scroll state independently.
  window.__sixthSenseMotion = { lenis, reducedMotion, isDesktop, hasGsapScroll };
})();
